/**
 * newCopy entries for instance-subtask errors (`ErroDataType` family).
 */
import { PARTICIPANT_ACTOR_KEY, TemplateResourceType } from '@dadohr/shared/domainTypes/experience';
import { getNestedKeyPart, isCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import React from 'react';
import { findParentTaskTemplate } from '../taskTemplateLookup';
import { CopyPack, Issue, IssueCopyContext, IssueKind } from '../types';
import {
  ctxOf,
  dataOfFamily,
  emailFieldLabel,
  genericRetrySupportSteps,
  HELP_ARTICLES,
  HelpArticle,
  importOrEditProfileSteps,
  personLabel,
  lmsNoRetryStep,
  retryStep,
  SupportEmail,
  taskLink,
  tt
} from './shared';
import { ukgWriteFailedCopy } from './ukg';

const dataOf = dataOfFamily('INSTANCE_SUBTASK');

const taskIdOf = (group: Issue[]): string | undefined =>
  findParentTaskTemplate(dataOf(group).tpl, dataOf(group).subtaskTplId)?.id;

/** The Docusign envelope template resource this subtask sends; undefined
 * once it has been removed from the experience template. */
const docusignResourceOf = (group: Issue[]): any => {
  const d = dataOf(group);
  const resource = (d.tpl?.data?.resources ?? []).find((r: any) => r.resourceId === d.resourceId);
  return resource?.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE ? resource : undefined;
};

const docusignTemplateNameOf = (group: Issue[]): string | undefined => docusignResourceOf(group)?.name;

/** "A, B" when Salesforce's rejection names the required fields it was
 * missing; undefined for any other message. */
const salesforceMissingFieldsOf = (group: Issue[]): string | undefined =>
  (dataOf(group).errorMessage || '').match(/Required fields? are missing:\s*\[([^\]]+)\]/i)?.[1];

/** Label of the profile field a background-check subtask reads the work
 * location from, recorded on the error. Falls back to the bare key, and to
 * a generic noun for records written before the backend sent the key. */
const workLocationFieldLabelOf = (group: Issue[]): string => {
  const c = ctxOf(group);
  const key = (dataOf(group) as { data?: { workLocationSourceField?: string } }).data?.workLocationSourceField;
  if (!key) return c.t('work location field');
  return c.userFieldLabel?.(key) || (isCustomFieldKey(key) ? getNestedKeyPart(key) : key);
};

/** "work email" / "personal email" per the address the signer is set to
 * use; plain "email" for records that didn't store it. */
const docusignEmailLabel = (group: Issue[]): string => {
  const c = ctxOf(group);
  const addressType = dataOf(group).addressType;
  return addressType ? emailFieldLabel(addressType) : c.t('email');
};

const assignSupportingActor__copy = {
  summary: (g: Issue[]) => {
    const c = ctxOf(g);
    const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
    return tt(c, `"{{ actor }}" role couldn't be assigned: no user with email address {{ workEmail }}`, {
      actor,
      workEmail: dataOf(g).workEmail
    });
  },
  whatCausedIt: (g: Issue[]) => {
    const c = ctxOf(g);
    const workEmail = dataOf(g).workEmail;
    return (
      <>
        {tt(c, 'When the task(s) listed below completed, the email address provided did not match any users in Dado:')}{' '}
        <strong>{workEmail}</strong>.{' '}
        {tt(c, 'This can happen if someone mistypes an email, or if not all of your employees are in Dado.')}
      </>
    );
  },
  howToFixIt: (g: Issue[]) => {
    const c = ctxOf(g);
    return (
      <ul>
        <li>
          {tt(c, 'If the email address provided is incorrect, click the participants listed below to open up each experience, and either:')}
          <ul>
            <li>
              {tt(c, 'assign a user to this role yourself')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
            </li>
            <li>
              {tt(c, 'uncomplete the task and resend it to the assignee')} <HelpArticle href={'https://help.dadohr.com/article/69-move-resend-or-skip-items-in-one-participants-experience#How-to-uncomplete-a-Task-thats-already-been-completed-jwwlL'} />
            </li>
          </ul>
        </li>
        <li>{tt(c, 'If you have assigned the role yourself, you can come back here and click ignore')}</li>
        <li>{tt(c, 'If the email address is correct, but the user does not exist in Dado, import them into Dado.')}</li>
        {retryStep()}
      </ul>
    );
  }
};

export const instanceSubtaskNewCopy = {
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND]: {
    category: 'Supporting Actor assignment failed',
    summary: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, `"{{ actor }}" role couldn't be assigned: this role doesn't exist in this experience`, {
        actor
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below completed, the')} <strong>{actor}</strong>{' '}
          {tt(
            c,
            'role did not exist on this experience, so it could not be assigned. This is likely because that role has been deleted or renamed.'
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} {taskLink(c, taskIdOf(g), tt(c, 'task'))}{' '}
            {tt(c, 'to specify the correct role')}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL]: {
    category: 'Supporting Actor assignment failed',
    ...assignSupportingActor__copy
  },
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND]: {
    category: 'Supporting Actor assignment failed',
    ...assignSupportingActor__copy
  },

  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the task(s) listed below started, an unknown issue prevented Dado from sending the background check invitation.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK__MISSING_PERSONAL_EMAIL]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: no personal email'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        "When the task(s) listed below started, the participant(s) didn't have a personal email address in Dado. Checkr requires this address, so Dado was not able to initiate the background check."
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if their personal email has since been added to Dado')}</li>
          <li>
            {tt(c, 'If this does not work, add their personal email by either:')}
            <ul>{importOrEditProfileSteps('personal email')}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK__PACKAGE_UNAVAILABLE]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: package not specified'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the task(s) listed below started, no background check package was selected in the task settings, so Dado was not able to initiate the background check.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} {taskLink(c, taskIdOf(g), tt(c, 'task(s)'))} {tt(c, 'to specify the relevant package(s)')}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK__MISSING_WORK_LOCATION]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: no work location'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, the')} <strong>{workLocationFieldLabelOf(g)}</strong>{' '}
          {tt(
            c,
            "on the participant(s)' profile was blank. Dado uses this field to specify the work location, which is required by Checkr. Without this data, Dado was not able to initiate the background check."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const fieldLabel = workLocationFieldLabelOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} <strong>{fieldLabel}</strong>{' '}
            {tt(c, 'for the participant(s) listed below by either:')}
            <ul>{importOrEditProfileSteps(fieldLabel)}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK__UNRECOGNIZED_WORK_LOCATION]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: invalid work location'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the task(s) listed below started, the work location did not contain a recognizable US state or (for international checks) country. Without this data, Dado was not able to initiate the background check.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const fieldLabel = workLocationFieldLabelOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} <strong>{fieldLabel}</strong>{' '}
            {tt(
              c,
              'for the participant(s) listed below to ensure it contains a recognizable US state or country, by either:'
            )}
            <ul>{importOrEditProfileSteps(fieldLabel)}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK__WORK_LOCATION_FIELD_NOT_CONFIGURED]: {
    category: 'Background Check Invitation failed',
    summary: (g) => tt(ctxOf(g), 'Background check couldn’t be initiated: work location not specified'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, the field Dado should use for')} <strong>{tt(c, 'work location')}</strong>{' '}
          {tt(c, 'had not been specified. This field is required by Checkr so, without it, Dado was not able to initiate the background check.')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} {taskLink(c, taskIdOf(g), tt(c, 'task(s)'))}{' '}
            {tt(c, "to specify which field contains data on the participants' work location")}
          </li>
          {retryStep()}
        </ul>
      );
    }
  },

  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN]: {
    category: 'Signature request failed',
    summary: (g) => tt(ctxOf(g), 'Signature request couldn’t be sent: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the task(s) listed below started, an unknown issue prevented Dado from sending the signature request.'),
    howToFixIt: () => genericRetrySupportSteps()
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE]: {
    category: 'Signature request failed',
    summary: (g) => {
      const c = ctxOf(g);
      const templateName = docusignTemplateNameOf(g);
      if (!templateName) return tt(c, 'Signature request couldn’t be sent: Docusign template not found');
      return tt(c, 'Signature request couldn’t be sent: Docusign template "{{ templateName }}" not found', { templateName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const templateName = docusignTemplateNameOf(g);
      const tail = tt(c, "This can happen if the template has been deleted or is not shared with Dado's Docusign user.");
      if (!templateName)
        return (
          <>
            {tt(c, 'When the task(s) listed below started, Dado could not find the specified template in Docusign.')} {tail}
          </>
        );
      return (
        <>
          {tt(c, 'When the task(s) listed below started, Dado could not find the')} <strong>{templateName}</strong>{' '}
          {tt(c, 'template in Docusign.')} {tail}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const templateName = docusignTemplateNameOf(g);
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if the Docusign template is now shared with Dado')}</li>
          <li>
            {templateName ? (
              <>
                {tt(c, 'If this does not work, check in Docusign that')} <strong>{templateName}</strong>{' '}
                {tt(c, 'still exists, and is shared with the Docusign account used by Dado.')}
              </>
            ) : (
              tt(
                c,
                'If this does not work, check in Docusign that the specified template still exists, and is shared with the Docusign account used by Dado. You can check the sub-task settings to see which template Dado is looking for.'
              )
            )}{' '}
            <HelpArticle href={'https://help.dadohr.com/article/120-sign-document-sub-task#Dado-cannot-access-the-template-in-your-eSignature-service-ZAKZ_'} />
          </li>
          <li>
            {tt(c, 'To find out the Docusign account Dado uses, see this')}{' '}
            <HelpArticle href={'https://help.dadohr.com/article/123-docusign-integration#Find-out-which-Docusign-account-is-used-by-Dado-your-Docusign-Integra-kgxfA'} />
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR]: {
    category: 'Signature request failed',
    summary: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, `Signature request couldn't be sent: "{{ actor }}" not assigned`, { actor });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, nobody was assigned as the')} <strong>{actor}</strong>{' '}
          {tt(
            c,
            "role in these experience(s). The signature request is set up to send to this role, and Dado can't initiate the request unless they're assigned."
          )}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(
              c,
              'Click the participants listed below to open up each experience, and check that the role is now assigned'
            )}
          </li>
          <li>
            {tt(c, 'If not, assign a user to this role')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL]: {
    category: 'Signature request failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, `Signature request couldn't be sent to {{ fullName }}: no {{ addressType }} in Dado`, {
        fullName,
        addressType: docusignEmailLabel(g)
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, {{ fullName }} didn’t have a {{ addressType }} set in Dado.', {
            fullName,
            addressType: docusignEmailLabel(g)
          })}{' '}
          {tt(c, "The signature request is set up to send to them, so Dado can't initiate the request without their email.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const label = docusignEmailLabel(g);
      return (
        <ul>
          <li>{tt(c, 'Click retry to see if their {{ addressType }} has since been added to Dado', { addressType: label })}</li>
          <li>
            {tt(c, 'If this does not work, add their {{ addressType }} by either:', { addressType: label })}
            <ul>{importOrEditProfileSteps(label)}</ul>
          </li>
          {retryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH]: {
    category: 'Signature request failed',
    summary: (g) => {
      const c = ctxOf(g);
      const templateName = docusignTemplateNameOf(g);
      if (!templateName) return tt(c, 'Signature request couldn’t be sent: role names have changed in Docusign');
      return tt(c, 'Signature request couldn’t be sent: role names have changed in Docusign template "{{ templateName }}"', {
        templateName
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const templateName = docusignTemplateNameOf(g);
      const tail = tt(
        c,
        'This can happen if someone updates the Docusign template to rename the signer/other recipient roles. When this happens, Docusign blocks Dado’s attempts to create a signature request.'
      );
      if (!templateName)
        return (
          <>
            {tt(c, 'When the task(s) listed below started, the names for the different roles in the signature process specified in Dado no longer matched the ones in Docusign.')}{' '}
            {tail}
          </>
        );
      return (
        <>
          {tt(c, 'When the task(s) listed below started, the role names specified in Dado no longer matched the ones in the')}{' '}
          <strong>{templateName}</strong> {tt(c, 'Docusign template.')} {tail}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Update the')} {taskLink(c, taskIdOf(g), tt(c, 'task'))}{' '}
            {tt(c, 'in Dado so the role names match what’s in Docusign')}{' '}
            <HelpArticle href={'https://help.dadohr.com/article/120-sign-document-sub-task#The-role-names-for-this-template-in-Docusign-have-been-updated-and-no-N8XhS'} />
          </li>
          {retryStep()}
        </ul>
      );
    }
  },

  [IssueKind.SALESFORCE_CONTACT_RESOLUTION_FAILED]: {
    category: 'Data sync failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, PARTICIPANT_ACTOR_KEY, dataOf(g).instance);
      return tt(c, "Data couldn't be synced to Salesforce: Contact couldn't be found or created for {{ fullName }}", {
        fullName
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const message = dataOf(g).errorMessage || '';
      return (
        <>
          {tt(
            c,
            'Dado tried to find or create a matching Salesforce record for this participant, but Salesforce rejected the request:'
          )}{' '}
          <em>{message}</em>
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, PARTICIPANT_ACTOR_KEY, dataOf(g).instance);
      const missingFields = salesforceMissingFieldsOf(g);
      return (
        <ul>
          {missingFields ? (
            <>
              <li>
                {tt(c, 'Make sure the following are included in the field mappings of the Salesforce data destination:')}{' '}
                <strong>{missingFields}</strong>
              </li>
              <li>
                {tt(c, "Ensure that the Dado fields set to update these Salesforce fields are filled out on {{ fullName }}'s user profile", {
                  fullName
                })}
              </li>
            </>
          ) : (
            <>
              <li>
                {tt(c, 'If there are specific missing fields, ensure the Salesforce data destination has these included in the field mappings.')}
              </li>
              <li>{tt(c, "Check that these fields are filled on {{ fullName }}'s user profile", { fullName })}</li>
            </>
          )}
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(
              c,
              "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
            )}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.SALESFORCE_WRITE_FAILED]: {
    category: 'Data sync failed',
    summary: (g) => tt(ctxOf(g), 'Data couldn’t be synced to Salesforce'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g) as { errorMessage?: string; actionTplId?: string };
      const opener = d.actionTplId
        ? tt(c, 'When the automation(s) listed below ran,')
        : tt(c, 'When the task(s) listed below completed,');
      return (
        <>
          {opener} {tt(c, 'Salesforce rejected the data Dado sent. Salesforce provided the following details:')}{' '}
          <em>{d.errorMessage || ''}</em>
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(
              c,
              "If the issue relates to invalid fields, verify the field still exists in Salesforce and that the Connected App's profile has Edit access to it."
            )}
          </li>
          <li>
            {tt(
              c,
              "If the issue relates to insufficient access, field integrity, or a required field, check the Connected App's field-level permissions and the field's validation rules in Salesforce."
            )}
          </li>
          <li>{tt(c, 'Check that the corresponding fields are filled on the user profiles for the participants listed below')}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(
              c,
              "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
            )}
          </li>
        </ul>
      );
    }
  },
  [IssueKind.UKG_WRITE_FAILED]: ukgWriteFailedCopy,
  [IssueKind.WORKDAY_WRITE_FAILED]: {
    category: 'Data sync failed',
    summary: (g) => tt(ctxOf(g), 'Data couldn’t be synced to Workday'),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g) as { errorMessage?: string; actionTplId?: string };
      const opener = d.actionTplId
        ? tt(c, 'When the automation(s) listed below ran,')
        : tt(c, 'When the task(s) listed below completed,');
      return (
        <>
          {opener} {tt(c, 'Workday rejected the data Dado sent. Workday provided the following details:')}{' '}
          <em>{d.errorMessage || ''}</em>
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const msg = (dataOf(g) as { errorMessage?: string }).errorMessage || '';
      // Same branches as the legacy copy: the worker's message carries either
      // the WORKDAY_WORKER_ID_NOT_FOUND code or Workday's HTTP status.
      const cause = /WORKDAY_WORKER_ID_NOT_FOUND/i.test(msg)
        ? tt(c, 'This person isn’t linked to a Workday worker. Check that the Workday import maps their Workday ID.')
        : /HTTP 4\d\d/.test(msg)
          ? tt(
              c,
              'Check the value’s format (reference fields need a Workday ID), or point the subtask’s data destination at a different field.'
            )
          : tt(c, 'Check the Workday integration connection and the field mapping.');
      return (
        <ul>
          <li>{cause}</li>
          {retryStep()}
          <li>
            {tt(c, "If this still doesn't work, contact")} <SupportEmail />{' '}
            {tt(
              c,
              "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
            )}
          </li>
        </ul>
      );
    }
  },

  [IssueKind.INSTANCE_SUBTASK__UNKNOWN]: {
    category: 'Task issue',
    summary: (g) =>
      tt(ctxOf(g), 'Unknown issue prevented a task starting: {{ taskName }}', { taskName: dataOf(g).timelineItem }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const taskName = dataOf(g).timelineItem;
      return (
        <>
          {tt(c, 'An unknown issue prevented the "{{ taskName }}" task starting as usual.', { taskName })}
        </>
      );
    },
    howToFixIt: () => genericRetrySupportSteps()
  },

  // ---- COMPLETE_LMS_MODULE ----
  [IssueKind.COMPLETE_LMS_MODULE__NO_ACTOR]: {
    category: 'LMS enrollment failed',
    summary: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, 'Docebo enrollment failed: "{{ actor }}" not assigned', { actor });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const actor = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, nobody was assigned as the')} <strong>{actor}</strong>{' '}
          {tt(c, "role in this experience, so Dado couldn't enroll them in the LMS module.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'Click the participants listed below to open up each experience, and check that the role is now assigned')}
          </li>
          <li>
            {tt(c, 'If not, assign a user to this role')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
          </li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__NO_EMAIL]: {
    category: 'LMS enrollment failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, 'Docebo enrollment failed for {{ fullName }}: no email address in Dado', { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, "When the task(s) listed below started, {{ fullName }} didn't have an email address in Dado.", { fullName })}{' '}
          {tt(c, "Dado enrolls people in Docebo courses using their email address, so without this address, the module couldn't be assigned.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, 'If this does not work, add an email address by either:')}
            <ul>{importOrEditProfileSteps()}</ul>
          </li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER]: {
    category: 'LMS enrollment failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, 'Docebo enrollment failed for {{ fullName }}: couldn’t find their Docebo account', { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, Dado couldn’t find a Docebo account for {{ fullName }}.', { fullName })}{' '}
          {tt(c, "This can sometimes happen if their email address in Dado doesn't match their Docebo account, or their Docebo account is inactive.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Confirm that they have an active account in Docebo. If not, set up their account.')}</li>
          <li>
            {tt(c, "If this does not work, confirm their email address matches what's in Docebo. If you need to update the email in Dado, you can do so by either:")}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__ENROLL_FAILED]: {
    category: 'LMS enrollment failed',
    summary: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return tt(c, 'Docebo enrollment failed for {{ fullName }}: enrollment failed', { fullName });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const fullName = personLabel(c, dataOf(g).actorKey, dataOf(g).instance);
      return (
        <>
          {tt(c, 'When the task(s) listed below started, Dado tried to enroll {{ fullName }} in the course in Docebo, but the enrollment request failed.', { fullName })}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, "In Docebo, check that the course settings and Dado's service account settings permit Dado to enroll someone in this course.")}
          </li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__UNIT_NOT_FOUND]: {
    category: 'LMS enrollment failed',
    summary: (g) => tt(ctxOf(g), 'Docebo enrollment failed: course no longer found in Docebo'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the task(s) listed below started, Dado couldn’t find the course specified in the sub-task settings in Docebo. Usually this means the course was deleted or renamed.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, "Check the course still exists in Docebo, and the Dado Docebo service account settings permit Dado to enroll someone in this course.")}
          </li>
          <li>{tt(c, 'Update the sub-task settings to specify the correct course')}</li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__INTEGRATION_BROKEN]: {
    category: 'LMS enrollment failed',
    summary: (g) => tt(ctxOf(g), 'Docebo enrollment failed: issues connecting to Docebo'),
    whatCausedIt: (g) =>
      tt(
        ctxOf(g),
        'When the task(s) listed below started, Dado had issues connecting to the Docebo API. This is usually a passing issue, but can also mean the Dado<>Docebo integration needs to be reconnected.'
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'Check the Docebo connection on the Integrations page, and reconnect it if needed')}</li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.COMPLETE_LMS_MODULE__UNKNOWN]: {
    category: 'LMS enrollment failed',
    summary: (g) => tt(ctxOf(g), 'Docebo enrollment failed: unknown issue'),
    whatCausedIt: (g) =>
      tt(ctxOf(g), 'When the task(s) listed below started, an unknown issue prevented the module from being assigned.'),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(c, "In Docebo, check that the course settings and Dado's service account settings permit Dado to enroll someone in this course.")}
          </li>
          {lmsNoRetryStep()}
        </ul>
      );
    }
  }
} satisfies Partial<CopyPack>;
